import { useState } from 'react'
import { addComment, deleteItem, updateItem } from '../api/client'
import ProjectPicker from './ProjectPicker'
import type { Comment, Item, Project, Status } from '../types'

const STATUSES: Status[] = ['inbox', 'next', 'blocked', 'waiting', 'someday', 'done', 'trash']
const CONTEXTS = ['@maison', '@pc', '@tel', '@courses']

interface Props {
  item: Item
  projects: Project[]
  onClose: () => void
  onChanged: () => void
  onReclarify: (id: string) => void
}

export default function EditSheet({ item, projects, onClose, onChanged, onReclarify }: Props) {
  const [title, setTitle] = useState(item.title)
  const [nextAction, setNextAction] = useState(item.next_action ?? '')
  const [domain, setDomain] = useState(item.domain ?? '')
  const [status, setStatus] = useState<Status>(item.status)
  const [context, setContext] = useState<string | null>(item.context)
  const [deadline, setDeadline] = useState(item.deadline ?? '')
  const [timeEstimate, setTimeEstimate] = useState(item.time_estimate?.toString() ?? '')
  const [note, setNote] = useState(item.note ?? '')
  const [projectId, setProjectId] = useState<string | null>(item.project_id)
  const [picking, setPicking] = useState(false)
  const [comments, setComments] = useState<Comment[]>(item.comments)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)

  async function save() {
    if (busy) return
    setBusy(true)
    try {
      await updateItem(item.id, {
        title: title.trim(),
        next_action: nextAction.trim() || null,
        domain: domain.trim() || null,
        status,
        context,
        deadline: deadline || null,
        time_estimate: timeEstimate ? parseInt(timeEstimate, 10) : null,
        note: note.trim() || null,
        project_id: projectId,
      })
      onClose()
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (busy) return
    setBusy(true)
    try {
      await deleteItem(item.id)
      onClose()
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  async function comment() {
    const text = draft.trim()
    if (!text || busy) return
    setBusy(true)
    try {
      const r = await addComment(item.id, text)
      setComments(r.comments)
      setDraft('')
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="panel">
        <h3>Modifier la tâche</h3>
        <div className="lbl">Titre</div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="lbl">Prochaine action</div>
        <input value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="(optionnel)" />
        <div className="lbl">Domaine</div>
        <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="Moto, Ecommerce, Admin…" />
        <div className="lbl">Projet</div>
        <div className="chips">
          <button className="chip on" onClick={() => setPicking(true)}>
            {projectId ? '📁 ' + (projects.find((p) => p.id === projectId)?.title ?? 'projet inconnu') : '— aucun'}
          </button>
        </div>
        <div className="lbl">Statut</div>
        <div className="chips">
          {STATUSES.map((s) => (
            <button key={s} className={'chip' + (s === status ? ' on' : '')} onClick={() => setStatus(s)}>
              {s}
            </button>
          ))}
        </div>
        <div className="lbl">Contexte</div>
        <div className="chips">
          {CONTEXTS.map((c) => (
            <button key={c} className={'chip' + (c === context ? ' on' : '')} onClick={() => setContext(c)}>
              {c}
            </button>
          ))}
          <button className={'chip' + (!context ? ' on' : '')} onClick={() => setContext(null)}>
            —
          </button>
        </div>
        <div className="lbl">Échéance (facultatif)</div>
        <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        <div className="lbl">Estimation en minutes (facultatif)</div>
        <input
          type="number"
          min="0"
          value={timeEstimate}
          onChange={(e) => setTimeEstimate(e.target.value)}
          placeholder="ex. 15"
        />
        <div className="lbl">Note (facultatif)</div>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="…" />
        <div className="lbl">Commentaires (facultatif)</div>
        <div className="comments">
          {comments.length === 0 ? (
            <div className="empty" style={{ padding: '2px 0' }}>
              Aucun
            </div>
          ) : (
            comments.map((c, i) => (
              <div className="cmt" key={i}>
                <span>{c.text}</span>
                <span className="cmt-d">{(c.at ?? '').slice(0, 10)}</span>
              </div>
            ))
          )}
        </div>
        <div className="cmt-add">
          <input
            placeholder="Ajouter un commentaire…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void comment()}
          />
          <button className="ebtn" onClick={() => void comment()}>
            +
          </button>
        </div>
        <button
          className="reclbtn"
          onClick={() => {
            onClose()
            onReclarify(item.id)
          }}
        >
          ↻ Re-clarifier avec le coach (→ projet)
        </button>
        <div className="actions">
          <button className="save" onClick={() => void save()}>
            Enregistrer
          </button>
          <button className="del" onClick={() => void remove()}>
            Supprimer
          </button>
          <button className="cancel" onClick={onClose}>
            Annuler
          </button>
        </div>
      </div>
      {picking && (
        <ProjectPicker
          current={projectId}
          projects={projects}
          onPick={(id) => {
            setProjectId(id)
            setPicking(false)
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}
